import { type ClientMessage, parseServerMessage, type ServerMessage } from "./protocol";

/** Thin wrapper over the browser WebSocket: typed messages in and out. */
export class NetClient {
	private readonly listeners = new Set<(message: ServerMessage) => void>();
	private readonly closeListeners = new Set<(code: number) => void>();

	private constructor(private readonly ws: WebSocket) {
		ws.addEventListener("message", (ev) => {
			const message = typeof ev.data === "string" ? parseServerMessage(ev.data) : null;
			if (!message) return;
			for (const listener of [...this.listeners]) listener(message);
		});
		ws.addEventListener("close", (ev) => {
			for (const listener of [...this.closeListeners]) listener(ev.code);
		});
	}

	/** Opens a connection; rejects if it can't be opened within `timeoutMs`. */
	static connect(url: string, timeoutMs = 8000): Promise<NetClient> {
		return new Promise((resolve, reject) => {
			let ws: WebSocket;
			try {
				ws = new WebSocket(url);
			} catch (err) {
				reject(err);
				return;
			}
			const timer = setTimeout(() => {
				ws.close();
				reject(new Error("timeout"));
			}, timeoutMs);
			ws.addEventListener(
				"open",
				() => {
					clearTimeout(timer);
					resolve(new NetClient(ws));
				},
				{ once: true },
			);
			ws.addEventListener(
				"close",
				() => {
					clearTimeout(timer);
					reject(new Error("closed"));
				},
				{ once: true },
			);
		});
	}

	get open(): boolean {
		return this.ws.readyState === WebSocket.OPEN;
	}

	/** Subscribes to server messages; returns the unsubscribe function. */
	onMessage(listener: (message: ServerMessage) => void): () => void {
		this.listeners.add(listener);
		return () => this.listeners.delete(listener);
	}

	onClose(listener: (code: number) => void): () => void {
		this.closeListeners.add(listener);
		return () => this.closeListeners.delete(listener);
	}

	send(message: ClientMessage): void {
		if (this.open) this.ws.send(JSON.stringify(message));
	}

	close(): void {
		this.listeners.clear();
		this.ws.close(1000);
	}
}
